import React, { useCallback } from 'react';
import Helmet from 'react-helmet';
import {
  Alert,
  Container,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { BurgerMenu, Header } from '../../components';
import {
  ACCEPT_CONTACT_MUTATION,
  GET_BLOCKED_USERS_QUERY,
  GET_MY_CONTACTS_QUERY,
  REMOVE_CONTACT_REQUEST_MUTATION,
} from '../../gql';
import { ContactDetail } from './ContactDetail';
import { ContactsMenu } from './ContactsMenu';
import { ContactsOverview } from './ContactsOverview';
import { BlockedUserRow } from './types';

const pageStyles = { width: '100%', minHeight: '100%' };
const contentStyles = { flexGrow: 1, minWidth: 0 };
const detailContentStyles = { flexGrow: 1, minWidth: 0 };

const Contacts = React.memo(() => {
  const intl = useIntl();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDetail = ![
    '/contacts',
    '/contacts/',
    '/contacts/requests',
    '/contacts/sent',
    '/contacts/blocked',
  ].includes(location.pathname);
  const { data, loading } = useQuery(GET_MY_CONTACTS_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const {
    data: blockedData,
    loading: blockedLoading,
    error: blockedError,
  } = useQuery<{ getBlockedUsers: BlockedUserRow[] }>(GET_BLOCKED_USERS_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const [acceptContact] = useMutation(ACCEPT_CONTACT_MUTATION, {
    refetchQueries: [{ query: GET_MY_CONTACTS_QUERY }],
  });
  const [removeContactRequest] = useMutation(REMOVE_CONTACT_REQUEST_MUTATION, {
    refetchQueries: [{ query: GET_MY_CONTACTS_QUERY }],
  });

  const handleAccept = useCallback(
    async (id: string) => {
      await acceptContact({ variables: { id } });
    },
    [acceptContact]
  );

  const handleRemoveRequest = useCallback(
    async (id: string) => {
      await removeContactRequest({ variables: { id } });
    },
    [removeContactRequest]
  );

  const blockedUsers = blockedData?.getBlockedUsers ?? [];
  const contacts = data?.getMyContacts?.contacts ?? [];
  const incomingRequests = data?.getMyContacts?.contactRequests ?? [];
  const sentRequests = data?.getMyContacts?.sentRequests ?? [];
  const overviewProps = {
    contacts,
    blockedUsers,
    incomingRequests,
    loading: loading || blockedLoading,
    onAccept: handleAccept,
    onRemoveRequest: handleRemoveRequest,
    sentRequests,
  };

  return (
    <div style={pageStyles}>
      <Helmet
        title={intl.formatMessage({
          id: 'contacts.pageTitle',
          defaultMessage: 'Contacts | Acolyte',
        })}
      />
      <Header
        mainIcon={
          isMobile && !isDetail ? <BurgerMenu menu={<ContactsMenu />} /> : null
        }
      />
      <Container maxWidth="xl">
        {isDetail ? (
          <Stack sx={detailContentStyles}>
            <Routes>
              <Route path=":contactId" element={<ContactDetail />} />
              <Route path="*" element={<Navigate replace to="/contacts" />} />
            </Routes>
          </Stack>
        ) : (
          <Stack direction="row">
            {!isMobile && <ContactsMenu />}
            <Stack sx={contentStyles}>
              {blockedError && (
                <Alert severity="error">{blockedError.message}</Alert>
              )}
              <Routes>
                <Route
                  index
                  element={
                    <ContactsOverview {...overviewProps} activeTab="contacts" />
                  }
                />
                <Route
                  path="requests"
                  element={
                    <ContactsOverview {...overviewProps} activeTab="incoming" />
                  }
                />
                <Route
                  path="sent"
                  element={
                    <ContactsOverview {...overviewProps} activeTab="sent" />
                  }
                />
                <Route
                  path="blocked"
                  element={
                    <ContactsOverview {...overviewProps} activeTab="blocked" />
                  }
                />
              </Routes>
            </Stack>
          </Stack>
        )}
      </Container>
    </div>
  );
});

Contacts.displayName = 'Contacts';

export { Contacts };
